<template>
  <div class="work-permit dashboard">
    <div class="tms-dashboard__panel-header flex-header">
      <div class="wp-dashboard_non-btn-container">
        <div class="tms-dashboard__panel-title-box">
          <div class="tms-dashboard__panel-title">
            {{ $t('currentWorkPermits') }}
          </div>
          <div class="tms-dashboard__panel-view-all">
            <router-link
              to="/work-permits?tag=today"
              class="tms-dashboard__panel-view-all-today"
            >
              {{ $t('viewAllTodayPermits') }} >
            </router-link>
            <router-link to="/work-permits">
              {{ $t('viewAll') }} >
            </router-link>
          </div>
        </div>
        <div class="tms-dashboard__panel-toolbar">
          <div class="handover-takeback__counter" />
          <div class="handover-takeback__counter--duration">
            <span>{{ $t('showLabel') }}:</span>
            <HKLSelect
              :options="wpTimeHklOptions"
              placeholder=""
              :showSearch="false"
              :value="selectedWpTimeValue"
              @change="updateWPs"
            />
          </div>
        </div>
      </div>
      <div class="wp-dashboard_btn-container">
        <button
          type="button"
          class="btn btn--darkblue wp-dashboard__toolbar-reset-filter"
          role="reset-button"
          @click="resetWpDashboardFilters"
        >
          {{ $t('action.resetFilter') }}
        </button>
        <button
          :disabled="filteredParsedWP.length === 0"
          class="btn--orange"
          @click="navToFirstWAPPRWp()"
        >
          {{ $i18n.t('action.startReview') }}
        </button>
      </div>
    </div>

    <div class="dashboard__table wp-dashboard-common-wrap wp-dashboard-common-table-theme">
      <CommonTable
        :key="dashboardTableResetKey"
        ref="wpDashboardCommonTable"
        :tableId="WP_DASHBOARD_TABLE_ID"
        :renderHeaderList="wpDashboardRenderHeaderList"
        :tableContentLists="filteredParsedWP"
        :needSearch="true"
        :needFilter="false"
        :defaultOrderKey="'code'"
        :defaultOrder="true"
        :yScrollAuto="true"
        :minWidth1800px="false"
        :getConditions="wpDashboardOnCommonTableConditions"
        :enterEvent="wpDashboardFilterEnterNoop"
        :interactionFunc="wpDashboardInteractionFunc"
        :orderMethods="wpDashboardOrderMethods"
        :getTableOrder="wpDashboardGetTableOrderNoop"
      />
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import moment from 'moment'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import CommonTable from '@/controls/CommonTable.vue'
import dateUtil from '@/utils/dateUtil'
import HttpCache from '@/utils/HttpCache'
import i18n from '@/utils/i18n'
import EventBus from '@/utils/eventBus'
import types from '@/store/mutation-types'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { permitCodeSortKey } from '@/utils/sortingHelper'
import filters from '@/filters/filters'
import HKLSelect from '@/controls/Select/HKLSelect'
import { filterInvalidWPInDashboard, parsedWPFieldToShow } from '@/modules/workPermits/utils'
import { getDashBoardWorkPermits } from '@/api/workPermit/request'
import { buildWorkPermitListNavigationEntry, navigate } from '@/api/workPermit/util'
import { WORK_PERMIT_TYPES } from '@/modules/workPermits/consts'
import {
  buildAllWorkPermitsProjectTypeFilterOptions,
  buildAllWorkPermitsStatusFilterOptions,
  buildAllWorkPermitsTypeFilterOptions,
} from '@/modules/workPermits/allWorkPermitsListFilterOptions'
import WorkPermitDashboardFiltersMixin, {
  WP_DASH_FILTER_ALL,
  wpDashboardFilterDefaults,
} from '@/modules/dashboards/WorkPermitDashboardFilters.mixin'
import { getDashboardWorkPermitTypeLabel } from '@/modules/dashboards/workPermitDashboardLabels'
import { wpTimeOptions, PROJECT_STATUS_FOR_WP } from './consts'

const WP_DASHBOARD_TABLE_ID = 'work-permit-dashboard-table'

export default {
  name: 'DashboardWorkPermitTable',
  components: {
    CommonTable,
    HKLSelect,
  },
  mixins: [WorkPermitDashboardFiltersMixin],
  data () {
    return {
      WP_DASHBOARD_TABLE_ID,
      wpTimeOptions,
      workPermits: [],
      emptyValue: this.$t('emptyValue'),
      scheduleIndex: 0,
      readOnly: false,
      dashboardTableResetKey: 0,
    }
  },
  computed: {
    ...mapGetters('workPermit', ['reviewQueue', 'currentReviewWorkPermit']),
    ...mapGetters('user', ['isEngineer']),
    ...mapGetters('regions', ['backEndRegionalEnv']),
    showHoistingModule () {
      return !!lodash.get(this.backEndRegionalEnv, 'moduleSwitch.hoistingModule')
    },
    typeFilterOptions () {
      return buildAllWorkPermitsTypeFilterOptions(this.showHoistingModule)
    },
    statusFilterOptions () {
      return buildAllWorkPermitsStatusFilterOptions(this.isEngineer)
    },
    projectTypeFilterOptions () {
      return buildAllWorkPermitsProjectTypeFilterOptions()
    },
    wpTimeHklOptions () {
      return this.wpTimeOptions.map(option => ({
        name: this.$options.filters.customTimeLabel(option),
        value: option.value,
      }))
    },
    selectedWpTimeValue () {
      return (this.wpTimeOptions[this.scheduleIndex] || {}).value
    },
    wpDashboardInteractionFunc () {
      return {
        code: (item) => this.navToDetail(item),
      }
    },
    wpDashboardOrderMethods () {
      return {
        code: (item) => permitCodeSortKey(item.code),
        contractor: (item) => String(item.contractor || '').toUpperCase(),
        PIC: (item) => String(item.PIC || '').toUpperCase(),
        projectType: (item) => String(item.projectType || '').toUpperCase(),
        type: (item) => String(item.type || '').toUpperCase(),
        location: (item) => this.wpDashboardSortLocation(item),
        status: (item) => item.status,
        startDate: (item) =>
          item.originData && item.originData.startDate
            ? new Date(item.originData.startDate).getTime()
            : 0,
      }
    },
    parsedWP () {
      return this.workPermits.map((wp) => {
        const parsedFields = parsedWPFieldToShow(wp)
        return {
          originData: wp,
          code: lodash.get(wp, 'code'),
          contractor: lodash.get(wp, 'contractor.name'),
          type: getDashboardWorkPermitTypeLabel(wp.type),
          hasHotWork: this.hotWorkRequired(wp),
          status: lodash.get(wp, 'status'),
          isExtRequest: wp.requestExtendDate ? this.$t('workPermit.extensionStatus') : null,
          id: lodash.get(wp, 'id'),
          startDate: dateUtil.formatDate(wp.startDate, true),
          allowLink: true,
          ...parsedFields,
        }
      })
    },
    filteredParsedWP () {
      return this.wpDashboardApplyFilters(this.parsedWP)
    },
    wpDashboardRenderHeaderList () {
      const cap = (s) => this.$options.filters.capitalize(s)
      return [
        {
          name: 'code',
          title: cap(i18n.t('workPermit.number')),
          inputType: 'input',
          defaultValue: this.emptyValue,
        },
        {
          name: 'contractor',
          title: cap(i18n.t('workPermit.contractor')),
          inputType: 'input',
          defaultValue: this.emptyValue,
          formatRule: (val, row) =>
            `${lodash.escape(val || '')} (${lodash.escape(row.tenantName || this.emptyValue)})`,
        },
        {
          name: 'PIC',
          title: cap(i18n.t('ProjectPIC')),
          inputType: 'input',
          defaultValue: this.emptyValue,
          cls: 'project_pic',
        },
        {
          name: 'projectType',
          title: cap(i18n.t('projectType')),
          inputType: 'select',
          multiLabel: 'label',
          searchable: true,
          options: this.projectTypeFilterOptions,
          defaultValue: this.emptyValue,
          cls: 'project_type',
        },
        {
          name: 'type',
          title: cap(i18n.t('workPermit.type')),
          inputType: 'select',
          searchable: true,
          multiLabel: 'label',
          options: this.typeFilterOptions,
          defaultValue: this.emptyValue,
          cls: 'type hotwork',
          formatRule: (displayType, row) => {
            const text = lodash.escape(displayType || '')
            const img = row.hasHotWork
              ? '<img src="/ic_hotwork.png" alt="hotwork">'
              : ''
            return `<span>${text}</span>${img}`
          },
        },
        {
          name: 'location',
          title: cap(`${i18n.t('workPermit.location')} (${i18n.t('workPermit.workArea')})`),
          inputType: 'input',
          defaultValue: this.emptyValue,
          formatRule: (val, row) => {
            const b = lodash.escape(String(row.building || ''))
            const f = lodash.escape(String(row.floors || ''))
            const l = lodash.escape(String(row.location || ''))
            return `${b} - ${f}<br>${l}`
          },
        },
        {
          name: 'status',
          title: cap(i18n.t('status.name')),
          inputType: 'select',
          searchable: true,
          multiLabel: 'label',
          options: this.statusFilterOptions,
          defaultValue: this.emptyValue,
          cls: 'wpstatus',
          customLabel: this.$options.filters.customStatusLabel,
          formatRule: (statusVal, row) => {
            const label = filters.customStatusLabel(row.status)
            const ext = row.isExtRequest
              ? `<p class="ext-status">${lodash.escape(row.isExtRequest)}</p>`
              : ''
            return `${label}${ext}`
          },
        },
        {
          name: 'startDate',
          title: cap(i18n.t('workPermit.startDate')),
          inputType: 'date',
          singleDatePicker: true,
          defaultValue: this.emptyValue,
          cls: 'start-date',
          datePickerCanPutTotop: false,
          disabledDate: false,
          formatRule: (v, row) => row.startDate || this.emptyValue,
        },
      ]
    },
  },
  async mounted () {
    this.readOnly = this.isReadOnlyUser()
    this.scheduleIndex = this.readOnly ? 0 : 3
    const curOpt = this.wpTimeOptions[this.scheduleIndex]
    await this.getWPsScheduledBefore(curOpt && curOpt.value)
    this.$nextTick(() => this.wpDashboardSyncMixinFromCommonTableRef())
  },
  methods: {
    ...mapActions('workPermit', ['setReviewQueue', 'dequeueReviewQueue']),
    ...mapActions('listNavigation', {
      setWorkPermitListNavigationSequence: 'setListNavigationSequence',
    }),
    ...mapMutations('workPermit', {
      setUnderBatch: types.WORKPERMIT_SET_UNDER_BATCH,
    }),
    wpDashboardFilterEnterNoop () {},
    wpDashboardGetTableOrderNoop () {},
    wpDashboardOnCommonTableConditions (fc) {
      this.wpDashboardSyncFiltersFromCommonTable(fc || {})
    },
    wpDashboardSyncMixinFromCommonTableRef () {
      const ct = this.$refs.wpDashboardCommonTable
      if (ct && ct.filterConditions) {
        this.wpDashboardSyncFiltersFromCommonTable(ct.filterConditions)
      }
    },
    wpDashboardSyncFiltersFromCommonTable (fc) {
      const str = (v) => (v == null ? '' : String(v))
      if (fc.code !== undefined) {
        this.wpDashFilterCode = fc.code && fc.code.value != null ? str(fc.code.value) : ''
      }
      if (fc.contractor !== undefined) {
        this.wpDashFilterContractor =
          fc.contractor && fc.contractor.value != null ? str(fc.contractor.value) : ''
      }
      if (fc.PIC !== undefined) {
        this.wpDashFilterPIC = fc.PIC && fc.PIC.value != null ? str(fc.PIC.value) : ''
      }
      if (fc.projectType !== undefined) {
        const v = fc.projectType && fc.projectType.value
        if (v && typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'val')) {
          this.wpDashFilterProjectType = v.val === '' || v.val == null ? WP_DASH_FILTER_ALL : v.val
        } else {
          this.wpDashFilterProjectType = WP_DASH_FILTER_ALL
        }
      }
      if (fc.type !== undefined) {
        const v = fc.type && fc.type.value
        if (v && typeof v === 'object' && Array.isArray(v.val)) {
          this.wpDashFilterType = [...v.val]
        } else {
          this.wpDashFilterType = []
        }
      }
      if (fc.location !== undefined) {
        this.wpDashFilterLocation =
          fc.location && fc.location.value != null ? str(fc.location.value) : ''
      }
      if (fc.status !== undefined) {
        const v = fc.status && fc.status.value
        if (v && typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'val')) {
          this.wpDashFilterStatus =
            v.val === '' || v.val == null ? WP_DASH_FILTER_ALL : v.val
        } else {
          this.wpDashFilterStatus = WP_DASH_FILTER_ALL
        }
      }
      if (fc.startDate !== undefined) {
        const raw = fc.startDate && fc.startDate.value
        if (raw == null || raw === '') {
          this.wpDashFilterStartDate = null
        } else if (Array.isArray(raw)) {
          this.wpDashFilterStartDate = raw[0] || null
        } else {
          this.wpDashFilterStartDate = raw
        }
      }
    },
    resetWpDashboardFilters () {
      this.$store.dispatch('tables/removeTableUiCache', { tableId: WP_DASHBOARD_TABLE_ID })
      Object.assign(this.$data, wpDashboardFilterDefaults())
      this.dashboardTableResetKey += 1
    },
    wpDashboardSortLocation (item) {
      const value = item.building
      return [String(value || '').toUpperCase(), item.floorIndex, item.location]
    },
    getVisibleWorkPermitDashboardRows () {
      const tableRows = lodash.get(this.$refs, 'wpDashboardCommonTable.formatList')
      const rows = tableRows && tableRows.length ? tableRows : this.filteredParsedWP
      return rows.map(row => row.originData).filter(Boolean)
    },
    setWorkPermitDashboardNavigationSequence (underBatch = false) {
      this.setWorkPermitListNavigationSequence({
        entries: this.getVisibleWorkPermitDashboardRows()
          .map(wp => {
            const hoistingId = wp && wp.type === WORK_PERMIT_TYPES.HOISTING ? wp.id : null
            return buildWorkPermitListNavigationEntry(wp, hoistingId, { underBatch })
          })
          .filter(Boolean),
      })
    },
    async navToFirstWAPPRWp () {
      const rawList = this.getVisibleWorkPermitDashboardRows()
      await this.setReviewQueue(rawList)
      this.setWorkPermitDashboardNavigationSequence(true)
      this.setUnderBatch(true)
      HttpCache.enable()
      await this.dequeueReviewQueue()
      this.navToDetail(this.currentReviewWorkPermit.context, true)
    },
    isReadOnlyUser () {
      let wpPerms = lodash.cloneDeep(tmsPermsTypes.workpermit)
      delete wpPerms.read
      const hasOtherPerms = [...Object.values(wpPerms)].some((access) => {
        return aclHelper.getAccess(access)
      })
      return !hasOtherPerms
    },
    updateWPs (value) {
      const nextIndex = this.wpTimeOptions.findIndex((rec) => rec.value === value)
      this.scheduleIndex = nextIndex === -1 ? 0 : nextIndex
      this.getWPsScheduledBefore(value && Number(value))
    },
    async getWPsScheduledBefore (value) {
      const excludeProjectStatus = [
        PROJECT_STATUS_FOR_WP.COMPLETED,
        PROJECT_STATUS_FOR_WP.CANCELLED,
        PROJECT_STATUS_FOR_WP.WITHDRAWN,
      ]
      let query = {
        readOnly: this.readOnly,
        excludeModels: { histories: true, workers: true },
        '^projectStatus': excludeProjectStatus.join(','),
        needDlp: true,
      }
      if (value) {
        const sixHoursMilisecond = 6 * 60 * 60 * 1000
        const today = moment().startOf('day').valueOf()
        const endDay = this.scheduleIndex === 0 ? today + value + sixHoursMilisecond : today + value
        query = {
          ...query,
          scheduleAfter: today,
          scheduleBefore: endDay,
        }
      }
      await this.getWPs(query)
    },
    navToDetail (item, underBatch = false) {
      let permit
      let hoistingId
      const originData = item.originData || item
      if (originData.type === WORK_PERMIT_TYPES.HOISTING) {
        permit = originData
        hoistingId = originData.id
      } else {
        permit = originData
      }
      if (!underBatch) {
        this.setWorkPermitDashboardNavigationSequence(false)
      }
      navigate(permit, hoistingId, { fromViewAll: true, underBatch })
    },
    hasReadAccess () {
      return aclHelper.getAccess(tmsPermsTypes.workpermit.read)
    },
    async getWPs (query) {
      if (this.hasReadAccess()) {
        EventBus.emit('module_loading_startLoading')
        const payload = await getDashBoardWorkPermits(query) || []
        EventBus.emit('module_loading_stopLoading')
        const filteredPalyload = filterInvalidWPInDashboard(payload)
        this.workPermits = lodash.orderBy(filteredPalyload, item => permitCodeSortKey(item.code), 'asc')
        this.setReviewQueue(this.workPermits)
      }
    },
    hotWorkRequired (item) {
      return item.type === WORK_PERMIT_TYPES.HOISTING && item.hotWork
    },
  },
}
</script>

<style lang="scss" scoped>
/*
 * Work permit dashboard table: all layout/skin for this view lives in this SFC.
 * Do not edit shared CommonTable / TableContent; child appearance is tuned only under
 * `.wp-dashboard-common-wrap` / `.wp-dashboard-common-table-theme` on this page (see also global
 * `.wp-dashboard-common-wrap .work-permit-dashboard-table_common-table...` in styles.scss).
 */

.dashboard__table {
  .mobile-device & {
    overflow-y: scroll;
    table {
      width: 1440px - 24px * 2;
    }
  }
}

.wide-dashboard .wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table.common-table.y-scroll-auto {
  max-height: none;
}

/*
 * CommonTable + TableContent defaults are not modified in shared components; this block adjusts
 * appearance for the engineer WP dashboard. Merged with c88d0e1 (PMS-207): #ededed shell/row lines,
 * header title wrap + stacked sort label/caret, rgba zebra, rem cell padding. Filter/search row rules
 * are post-c88 additions (needSearch on this view).
 */
.wp-dashboard-common-table-theme {
  width: 100%;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table.common-table {
  margin: 0;
  width: 100%;
  border: solid #ededed;
  border-width: 1px 1px 0 1px;
  box-sizing: border-box;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-table-header-container {
  width: 100%;
  background-color: #fff;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-table-header-container tr {
  border-left: none;
  border-right: none;
}

/* Header row — c88d0e1: title cells wrap/clamp; no per-th bottom border (row borders come from CommonTable tr rules + wrap tweaks). */
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-title .common-table__head-th {
  font-size: 14px;
  color: #0c3a58;
  font-weight: bold;
  text-transform: uppercase;
  padding: 10px 10px 10px 20px;
  vertical-align: middle;
  background-color: #fff;
  box-sizing: border-box;
  overflow: hidden;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-title .common-table__head-th.hand {
  cursor: pointer;
  user-select: none;
}

/* Label + sort caret on one row; text may wrap inside its box, caret stays beside (not below). */
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-title .common-table__head-th > span {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Sort caret — match `common.scss` triangle size (dev uses 6px height); CommonTable defaults to 10px. */
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .triangle-up {
  border-bottom-width: 6px;
  border-bottom-color: #0c3a58;
  flex-shrink: 0;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .triangle-down {
  border-top-width: 6px;
  border-top-color: #0c3a58;
  flex-shrink: 0;
}

/* Filter row — live filters, not uppercase */
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter th {
  font-weight: normal;
  text-transform: none;
  vertical-align: middle;
  padding: 10px 10px 10px 20px;
  background-color: #fff;
  border-bottom: solid 1px #ededed;
  box-sizing: border-box;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter .search-filter-input {
  width: 100%;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter .search-area input.form-control,
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter .input-area input {
  width: 100%;
  box-sizing: border-box;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter .select-area .hkl-select {
  width: 100%;
}

/*
 * Filter row `th` uses normal weight for dashboard layout; dropdown options inherit `th` font-weight.
 * Tenant-projects (`datatable.scss th { font-weight: bold }`) yields bold dropdown containers — align controls here.
 */
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter .select-area .hkl-select,
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter .select-area .hkl-select .ant-select-selection,
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter .select-area .hkl-select .ant-select-selection__rendered {
  font-weight: bold;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter .date-area .vdp-datepicker {
  width: 100%;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-header-filter .date-area .date-picker-class {
  width: 100%;
  box-sizing: border-box;
}

/* Body — c88d0e1: #ededed row dividers, rgba zebra, tenant-style cell padding */
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .table-content tr {
  border-left: none;
  border-right: none;
  border-bottom: solid 1px #ededed;
  align-items: center;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .table-content tr:nth-child(odd) {
  background-color: rgba(235, 235, 235, 0.2);
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .table-content tr:nth-child(even) {
  background-color: #fff;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .table-content td {
  color: #0c3a58;
  padding: .625rem .625rem .625rem 1.25rem;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: inherit;
  box-sizing: border-box;
}

/* Permit no. link — match dashboard `.link_text` + blue accent like dev */
.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .table-content .underline {
  text-decoration: underline;
  cursor: pointer;
  color: #337ab7;
}

.wp-dashboard-common-table-theme ::v-deep .work-permit-dashboard-table_common-table .common-table-content-container td.type.hotwork {
  text-overflow: unset;
}

/*
 * Column shares (hklpmsr-dev): 15/19/8/8/10/20/8/12.
 * CommonTable sets `th` class from `item.name` (PIC, projectType, status, startDate); body `td` uses `cls`.
 */
.wp-dashboard-common-wrap {
  width: 100%;
}

/*
 * c88d0e1 (PMS-207): popovers/dropdowns escape clipping — scoped to this dashboard table instance only.
 * Duplicates intent of styles.scss global rule for the same selector; kept here so c88 styling is self-contained.
 */
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table.common-table.y-scroll-auto {
  overflow: visible;
}

/* c88d0e1: softer row separators on CommonTable `tr` inside this shell */
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table.common-table tr {
  border-bottom-color: #ededed;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table.common-table .common-table-header-container tr:first-child {
  border-top: none;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table tr.common-header-title,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table tr.common-header-filter {
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table tr.common-header-title th,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table tr.common-header-filter th {
  display: block;
  flex-shrink: 1;
  min-width: 0;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content tr {
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  box-sizing: border-box;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content td {
  display: block;
  flex-shrink: 1;
  min-width: 0;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-table__head-th.code,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-header-filter th.code,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content td.code {
  flex: 0 0 15%;
  max-width: 15%;
  width: 15%;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-table__head-th.contractor,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-header-filter th.contractor,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content td.contractor {
  flex: 0 0 19%;
  max-width: 19%;
  width: 19%;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-table__head-th.PIC,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-header-filter th.PIC,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content td.project_pic {
  flex: 0 0 8%;
  max-width: 8%;
  width: 8%;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-table__head-th.projectType,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-header-filter th.projectType,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content td.project_type {
  flex: 0 0 8%;
  max-width: 8%;
  width: 8%;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-table__head-th.type,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-header-filter th.type,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content td.type {
  flex: 0 0 10%;
  max-width: 10%;
  width: 10%;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-table__head-th.location,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-header-filter th.location,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content td.location {
  flex: 0 0 20%;
  max-width: 20%;
  width: 20%;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-table__head-th.status,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-header-filter th.status,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content td.wpstatus {
  flex: 0 0 8%;
  max-width: 8%;
  width: 8%;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-table__head-th.startDate,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .common-header-filter th.startDate,
.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .table-content td.start-date {
  flex: 0 0 12%;
  max-width: 12%;
  width: 12%;
}

.wp-dashboard-common-wrap ::v-deep .work-permit-dashboard-table_common-table .ext-status {
  color: #e49405;
  margin: 0;
}

.flex-header {
  display: flex;
}

/*
 * handover-takeback__counter / __counter--duration: previously came from @import hotb-list (removed).
 * Scope to this dashboard only — does not modify shared components.
 */
.work-permit.dashboard .handover-takeback__counter {
  display: flex;
}

.work-permit.dashboard .handover-takeback__counter--duration {
  display: flex;
  font-size: 12px;

  & * {
    font-size: inherit;
  }

  & > span {
    margin-right: 5px;
  }

  & .hkl-select {
    box-sizing: border-box;
    width: 95px;
    min-width: 95px;
    max-width: 95px;
    min-height: 20px;
    height: 20px;
    border: none;
    background-color: transparent;
    color: #0c3a58;

    ::v-deep .ant-select-selection {
      min-height: 20px;
      height: 20px;
      background-color: #ebebeb33;
    }

    ::v-deep .ant-select-selection__rendered {
      min-height: 20px;
      line-height: 20px;
      margin-left: 0;
      margin-right: 22px;
      text-align: right;
    }

    ::v-deep .ant-select-arrow {
      margin-top: -8px;
    }
  }
}

.wp-dashboard_non-btn-container {
  flex: auto;
}

.wp-dashboard_btn-container {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

</style>
